import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/core/basic/Button";
import { css } from "../../../../styled-system/css";
import { errorToast, successToast } from "../../../utils/toasts";
import { FaCircleCheck, FaCopy, FaDownload, FaTriangleExclamation } from "react-icons/fa6";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { formField } from "../../../../styled-system/recipes";
import { apiClient } from "../../../zodios/client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "../../../config/react-query";
import ChangePasswordForm from "../components/ChangePasswordForm";
import { FaX } from "react-icons/fa6";
import MFASetupFlow from "../components/MFASetupFlow";

const SecuritySettingsSection = () => {
  const { t } = useTranslation(["settings"]);
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const [generateNewMfaCode, setGenerateNewMfaCode] = useState(false);

  const inputClasses = formField();

  const mfaCodeQuery = useQuery({
    queryKey: ["mfaCodeInfo"],
    queryFn: async () => {
      return await apiClient.getMFACodeInfo();
    },
    enabled: false,
  });

  const mfaManagementQuery = useQuery({
    queryKey: ["mfaManagement"],
    queryFn: async () => {
      return await apiClient.getMFADeviceManagement();
    },
  });

  const mfaDeviceSetup = mfaManagementQuery.data?.totp_devices?.[0];

  const { otpauthurl, secret, digits } = mfaCodeQuery.data ?? {};

  const saveMfaCodeMutation = useMutation({
    mutationFn: async ({ otp }: { otp: string }) => {
      if (!secret || !digits) {
        throw new Error(t(($) => $.settings.mfa["MFA setup data is incomplete."]));
      }

      const body = { secret, digits, device_type: "default", otp };
      return await apiClient.confirmMFADeviceSetup(body);
    },
    onSuccess: () => {
      successToast({
        message: t(($) => $.settings.mfa["Two-factor authentication has been enabled for your account."]),
      });
      void queryClient.invalidateQueries({ queryKey: ["mfaManagement"] });
      setGenerateNewMfaCode(false);
    },
    onError: (error) => {
      errorToast({
        message: t(($) => $.settings.mfa["Failed to enable two-factor authentication, please retry."]),
        error,
      });
    },
  });

  const deleteMfaDeviceMutation = useMutation({
    mutationFn: async () => {
      if (!mfaDeviceSetup) {
        throw new Error(t(($) => $.settings.mfa["No MFA device to delete."]));
      }
      return await apiClient.deleteMFADevice({ totp_devices: [{ id: mfaDeviceSetup.id }] });
    },
    onSuccess: () => {
      successToast({
        message: t(($) => $.settings.mfa["Two-factor authentication has been disabled for your account."]),
      });
      void queryClient.invalidateQueries({ queryKey: ["mfaManagement"] });
    },
    onError: (error) => {
      errorToast({
        message: t(($) => $.settings.mfa["Failed to disable two-factor authentication, please retry."]),
        error,
      });
    },
  });

  return (
    <VStack css={{ gap: "medium", alignItems: "start", paddingY: "medium", fontSize: "medium" }}>
      <h4
        className={css({
          margin: 0,
          paddingX: "large",
          fontWeight: 500,
        })}
      >
        {t(($) => $.settings.security["Security Settings"])}
      </h4>
      <VStack gap={"large"} alignItems={"start"} maxWidth={"90%"} padding={"large"}>
        <label className={inputClasses.label}>{t(($) => $.settings.mfa["Two-factor Authentication"])}</label>
        <p className={css({ fontSize: "medium" })}>
          <FaTriangleExclamation />{" "}
          {t(
            ($) =>
              $.settings.mfa[
                "Two-factor authentication will only be used if you sign in with a username and password."
              ],
          )}
        </p>
        {generateNewMfaCode && otpauthurl && secret && digits ?
          <MFASetupFlow
            otpauthurl={otpauthurl}
            secret={secret}
            digits={digits}
            onCancel={() => {
              setGenerateNewMfaCode(false);
            }}
            onConfirm={(otp) => saveMfaCodeMutation.mutate({ otp })}
          />
        : saveMfaCodeMutation.isSuccess ?
          <VStack gap={"medium"} alignItems={"center"} textAlign={"center"}>
            <FaCircleCheck
              className={css({
                color: "trellisPalette.12",
                fontSize: 48,
              })}
            />
            <p className={css({ fontSize: "medium" })}>
              {t(($) => $.settings.mfa["Two-factor authentication is successfully set up for your account."])}
            </p>
            {saveMfaCodeMutation.data.recovery_tokens && (
              <>
                <p className={css({ fontSize: "medium" })}>
                  {t(
                    ($) =>
                      $.settings.mfa[
                        "Please save the following recovery codes in a safe place. You will need these if you lose access to your authenticator device."
                      ],
                  )}
                </p>

                <Flex
                  className={css({
                    gap: "small",
                    border: "1px solid lightgray",
                    borderRadius: "8px",
                    marginBottom: "medium",
                    padding: "medium",
                    backgroundColor: "#f9f9f9",
                  })}
                >
                  {saveMfaCodeMutation.data.recovery_tokens.map((code, index) => (
                    <code key={index} className={css({ fontSize: "medium" })}>
                      {code}
                    </code>
                  ))}
                  <Button
                    variant="contentOnly"
                    tooltip={t(($) => $.settings.mfa["Click to copy recovery codes to clipboard"])}
                    onPress={async () => {
                      if (!saveMfaCodeMutation.data.recovery_tokens) return;
                      await navigator.clipboard.writeText(saveMfaCodeMutation.data.recovery_tokens.join(","));
                      successToast({
                        message: t(($) => $.settings.mfa["Codes copied to clipboard"]),
                      });
                    }}
                  >
                    <FaCopy />
                  </Button>
                  <Button
                    variant="contentOnly"
                    tooltip={t(($) => $.settings.mfa["Click to download recovery codes"])}
                    onPress={() => {
                      if (!saveMfaCodeMutation.data.recovery_tokens) return;

                      const text = saveMfaCodeMutation.data.recovery_tokens.join("\n");
                      const blob = new Blob([text], { type: "text/plain" });
                      const url = URL.createObjectURL(blob);

                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "trellis-mfa-recovery-codes.txt";
                      a.click();

                      URL.revokeObjectURL(url);
                      successToast({
                        message: t(($) => $.settings.mfa["Recovery codes downloaded"]),
                      });
                    }}
                  >
                    <FaDownload />
                  </Button>
                </Flex>
              </>
            )}
            <Button
              onPress={() => {
                setGenerateNewMfaCode(false);

                saveMfaCodeMutation.reset();
              }}
            >
              {t(($) => $.settings.mfa.Done)}
            </Button>
          </VStack>
        : <VStack gap={"medium"} alignItems={"flex-start"}>
            {mfaDeviceSetup ?
              <>
                <p className={css({ fontSize: "medium" })}>
                  {t(($) => $.settings.mfa["Two-factor authentication is successfully set up for your account."])}
                </p>

                <Button
                  onPress={() => {
                    deleteMfaDeviceMutation.mutate();
                  }}
                >
                  {t(($) => $.settings.mfa["Disable 2FA"])}
                </Button>
              </>
            : <>
                <p className={css({ fontSize: "medium" })}>
                  {t(($) => $.settings.mfa["Two-factor authentication is not set up for your account."])}
                </p>
                <Button
                  onPress={() => {
                    setGenerateNewMfaCode(true);
                    void mfaCodeQuery.refetch();
                  }}
                >
                  {generateNewMfaCode ?
                    t(($) => $.settings.mfa["Regenerate 2FA Setup"])
                  : t(($) => $.settings.mfa["Set Up Two-Factor Authentication"])}
                </Button>
              </>
            }
          </VStack>
        }
      </VStack>
      <VStack alignItems={"start"} padding={"large"}>
        <Flex justifyContent={"space-between"} alignItems={"center"} width={"400px"}>
          <label className={inputClasses.label}>{t(($) => $.settings.mfa.Password)}</label>{" "}
          {showChangePasswordForm && (
            <Button variant={"contentOnly"} onPress={() => setShowChangePasswordForm(false)}>
              <FaX />
            </Button>
          )}
        </Flex>
        {showChangePasswordForm ?
          <ChangePasswordForm
            closeForm={() => {
              setShowChangePasswordForm(false);
            }}
          />
        : <Button
            onPress={() => {
              setShowChangePasswordForm(true);
            }}
          >
            {t(($) => $.settings.changePassword["Change Password"])}
          </Button>
        }
      </VStack>
    </VStack>
  );
};

export default SecuritySettingsSection;
